import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;
let bridgeStarted = false;
let lastAnalyticsConsent = null;

function readTermlyAnalyticsConsent() {
  try {
    const t = (typeof window !== 'undefined') ? window.Termly : null;
    if (!t) return null;
    if (typeof t.getConsentState === 'function') {
      const state = t.getConsentState() || {};
      if (typeof state.analytics === 'boolean') return state.analytics;
    }
    if (typeof t.consentState === 'object' && t.consentState) {
      if (typeof t.consentState.analytics === 'boolean') return t.consentState.analytics;
    }
  } catch {}
  return null;
}

export function hasAnalyticsConsent() {
  return readTermlyAnalyticsConsent() === true;
}

export function initPostHog() {
  if (initialized || !KEY) return;
  if (typeof window === 'undefined') return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      disable_session_recording: false,
      opt_out_capturing_by_default: true,
      loaded: (ph) => {
        // PostHog `init` is async — by the time this fires, the user
        // may already have granted consent (in which case `applyConsent`
        // called `opt_in_capturing()` before we were ready). Re-check
        // the current Termly state and reconcile, so we don't clobber
        // a fresh opt-in or accidentally opt-in after a revoke.
        try {
          if (readTermlyAnalyticsConsent() === true) {
            ph.opt_in_capturing();
          } else {
            ph.opt_out_capturing();
          }
        } catch {}
      },
    });
    initialized = true;
  } catch (e) {
    // Swallow errors so analytics never breaks the app
  }
}

function applyConsent(analyticsGranted) {
  if (analyticsGranted === lastAnalyticsConsent) return;
  lastAnalyticsConsent = analyticsGranted;
  if (analyticsGranted) {
    initPostHog();
    try { posthog.opt_in_capturing(); } catch {}
  } else if (initialized) {
    try { posthog.opt_out_capturing(); } catch {}
  }
}

// Bridge Termly consent decisions into PostHog. Termly exposes consent
// via `window.Termly.getConsentState()` once it loads, and emits a
// `consent` event on both the window and document objects whenever the
// user changes their preferences. We poll briefly on startup to catch
// the initial state, then rely on the event for updates.
export function startConsentBridge() {
  if (bridgeStarted || typeof window === 'undefined') return;
  bridgeStarted = true;

  const tick = () => {
    const v = readTermlyAnalyticsConsent();
    if (v !== null) applyConsent(v);
  };

  tick();
  let tries = 0;
  const iv = setInterval(() => {
    tries += 1;
    tick();
    // Stop polling once we have a definite answer or after ~15s.
    if (lastAnalyticsConsent !== null || tries > 60) clearInterval(iv);
  }, 250);

  const onConsent = () => tick();
  try { window.addEventListener('consent', onConsent); } catch {}
  try { document.addEventListener('consent', onConsent); } catch {}
}

export function track(event, props) {
  if (!initialized || !hasAnalyticsConsent()) return;
  try { posthog.capture(event, props); } catch {}
}

export function identify(userId, props) {
  if (!initialized || !hasAnalyticsConsent()) return;
  try { posthog.identify(userId, props); } catch {}
}

export function resetUser() {
  if (!initialized) return;
  try { posthog.reset(); } catch {}
}

export default posthog;
